// src/api/mockData.js
import dayjs from "dayjs";

const POSTS_KEY = "my_blog_posts_v1";
const USERS_KEY = "my_blog_users_v1";
const USER_KEY = "my_blog_user_v1";
const BOOKMARKS_KEY = "my_blog_bookmarks_v1";

function seedIfEmpty() {
  const raw = localStorage.getItem(POSTS_KEY);
  if (raw) return;

  const now = Date.now();
  const demo = [
    {
      id: String(now - 100000),
      title: "欢迎来到我的博客",
      summary: "这是 React + Ant Design 的前端博客 Demo（数据存在 localStorage）。",
      category: "博客导览",
      tags: ["React", "Antd"],
      cover:
        "radial-gradient(1200px 420px at 0% 0%, rgba(125,182,255,0.65) 0%, rgba(125,182,255,0) 60%), linear-gradient(135deg, #e6f0ff 0%, #fff1f7 100%)",
      content:
        "功能：\n- 首页文章列表 + 搜索 + 标签筛选\n- 登录后可写文章\n- 文章详情页\n- 归档（按月份）\n\n后续接后端，只需要替换 api 层实现。",
      createdAt: now - 100000,
      updatedAt: now - 100000,
    },
    {
      id: String(now - 500000),
      title: "归档页是怎么分组的？",
      summary: "用 dayjs 把 createdAt 格式化成 YYYY-MM 做分组。",
      category: "功能说明",
      tags: ["dayjs", "归档"],
      cover:
        "radial-gradient(900px 420px at 100% 0%, rgba(255,154,192,0.6) 0%, rgba(255,154,192,0) 55%), linear-gradient(135deg, #fff5e8 0%, #eafdf8 100%)",
      content:
        "归档逻辑：\n1）取所有文章\n2）key = dayjs(createdAt).format('YYYY-MM')\n3）按 key 分组\n4）组内按时间倒序\n",
      createdAt: now - 500000,
      updatedAt: now - 500000,
    },
  ];

  localStorage.setItem(POSTS_KEY, JSON.stringify(demo));
}

function seedUsersIfEmpty() {
  const raw = localStorage.getItem(USERS_KEY);
  if (raw) return;
  const now = Date.now();
  const demoUsers = [
    {
      id: String(now - 1),
      username: "admin",
      password: "123456",
      email: "admin@example.com",
      bio: "管理员账号（演示用）",
      role: "管理员",
      createdAt: now - 1,
      avatar: "",
    },
  ];
  localStorage.setItem(USERS_KEY, JSON.stringify(demoUsers));
}

function readPosts() {
  seedIfEmpty();
  const raw = localStorage.getItem(POSTS_KEY);
  try {
    const arr = JSON.parse(raw) || [];
    return arr.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

function writePosts(list) {
  localStorage.setItem(POSTS_KEY, JSON.stringify(list));
}

function readUsers() {
  seedUsersIfEmpty();
  const raw = localStorage.getItem(USERS_KEY);
  try {
    return JSON.parse(raw) || [];
  } catch {
    return [];
  }
}

function writeUsers(list) {
  localStorage.setItem(USERS_KEY, JSON.stringify(list));
}

// ========= Posts API =========
export async function listPosts() {
  return readPosts();
}

export async function getPost(id) {
  return readPosts().find((p) => String(p.id) === String(id)) || null;
}

export async function createPost({
  title,
  summary = "",
  category = "",
  tags = [],
  content,
  cover = "",
  createdAt: createdAtInput,
}) {
  const now = Date.now();
  const createdAt = Number.isFinite(createdAtInput) ? createdAtInput : now;
  const post = {
    id: String(now),
    title,
    summary,
    category,
    tags,
    content,
    cover,
    createdAt,
    updatedAt: createdAt,
    createdAtText: dayjs(createdAt).format("YYYY-MM-DD HH:mm"),
  };

  const list = readPosts();
  writePosts([post, ...list]);
  return post;
}

export async function updatePost(id, patch) {
  const list = readPosts();
  const next = list.map((p) => {
    if (String(p.id) !== String(id)) return p;
    return {
      ...p,
      ...patch,
      updatedAt: Date.now(),
    };
  });
  writePosts(next);
  return next.find((p) => String(p.id) === String(id)) || null;
}

export async function removePost(id) {
  const next = readPosts().filter((p) => String(p.id) !== String(id));
  writePosts(next);
  return true;
}

// ========= Auth API =========
export function isAuthed() {
  return !!localStorage.getItem(USER_KEY);
}

export function getCurrentUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function updateCurrentUser(patch) {
  const current = getCurrentUser() || {};
  const next = { ...current, ...patch };
  localStorage.setItem(USER_KEY, JSON.stringify(next));
  const users = readUsers();
  const updated = users.map((u) => {
    if (next.id && u.id === next.id) return { ...u, ...patch };
    if (!next.id && u.username === next.username) return { ...u, ...patch };
    return u;
  });
  writeUsers(updated);
  return next;
}

export async function login({ username, password }) {
  if (!username || !password) return { ok: false, msg: "请输入账号密码" };
  const users = readUsers();
  const found = users.find(
    (u) => u.username === username && u.password === password
  );
  if (!found) return { ok: false, msg: "账号或密码错误" };
  const safeUser = { ...found };
  delete safeUser.password;
  localStorage.setItem(USER_KEY, JSON.stringify(safeUser));
  return { ok: true, user: safeUser };
}

export async function register({ username, password }) {
  if (!username || !password) return { ok: false, msg: "请输入账号密码" };
  const users = readUsers();
  if (users.some((u) => u.username === username)) {
    return { ok: false, msg: "账号已存在" };
  }
  const now = Date.now();
  const user = {
    id: String(now),
    username,
    password,
    email: "",
    bio: "",
    role: "普通用户",
    createdAt: now,
    avatar: "",
  };
  writeUsers([user, ...users]);
  const safeUser = { ...user };
  delete safeUser.password;
  localStorage.setItem(USER_KEY, JSON.stringify(safeUser));
  return { ok: true, user: safeUser };
}

export function logout() {
  localStorage.removeItem(USER_KEY);
}

// ========= Bookmarks API =========
export function listBookmarks() {
  const raw = localStorage.getItem(BOOKMARKS_KEY);
  try {
    const arr = JSON.parse(raw) || [];
    return arr.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

export function addBookmark({ postId, title, scrollTop = 0 }) {
  const now = Date.now();
  const entry = {
    id: String(now),
    postId: String(postId),
    title: title || "未命名文章",
    scrollTop: Math.max(0, Number(scrollTop) || 0),
    createdAt: now,
  };
  const list = listBookmarks();
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify([entry, ...list]));
  return entry;
}
